import { Component } from '@theme/component';
import { debounce, oncePerEditorSession, prefersReducedMotion } from '@theme/utilities';

/**
 * Lens tint slider component with color and light modes.
 *
 * The glasses image (a white transparent PNG) doubles as a CSS mask for a
 * fill layer. The slider interpolates the fill color:
 * - Color mode blends across three anchor colors (start / mid / end).
 * - Light mode blends between two anchor colors (start / end).
 *
 * @typedef {object} SutyoLensTintRefs
 * @property {HTMLInputElement} slider - The range input element
 *
 * @typedef {[number, number, number]} RgbColor
 *
 * @typedef {object} FillStop
 * @property {RgbColor} color - The anchor color
 * @property {number} alpha - The anchor alpha, 0 to 1
 *
 * @extends {Component<SutyoLensTintRefs>}
 */
export class SutyoLensTintComponent extends Component {
  requiredRefs = ['slider'];

  /** @type {((() => void) & { cancel(): void }) | null} */
  #copyRemeasure = null;

  connectedCallback() {
    super.connectedCallback();

    this.colorStops = this.#parseStops();
    this.lensGradient = Math.min(Math.max(Number(this.dataset.lensGradient) || 0, 0), 100) / 100;
    this.hasAnimated = false;

    this.#setMode(this.dataset.activeMode === 'light' ? 'light' : 'color', { sync: false });
    this.sync();
    this.#setupHintAnimation();
    this.#measureCopySlot();
    this.#syncComposedLayout();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(() => {
          this.#measureCopySlot();
          this.#syncComposedLayout();
        })
        .catch(() => {});
    }

    this.#copyRemeasure = debounce(() => {
      this.#measureCopySlot();
      this.#syncComposedLayout();
    }, 200);
    window.addEventListener('resize', this.#copyRemeasure);

    this.#amplifyGlassesAlpha();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.#copyRemeasure) {
      window.removeEventListener('resize', this.#copyRemeasure);
      this.#copyRemeasure.cancel();
      this.#copyRemeasure = null;
    }
  }

  /**
   * Parse a hex color string into an RGB triplet.
   *
   * @param {string | undefined} value - The hex color, e.g. "#ff0000"
   * @param {RgbColor} fallback - The fallback color when parsing fails
   * @returns {RgbColor} The parsed RGB triplet
   */
  #parseColor(value, fallback) {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(value ?? '').trim());

    if (!match) {
      return fallback;
    }

    const parsed = Number.parseInt(match[1], 16);

    return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
  }

  /**
   * Collect the anchor colors and alphas for both modes from the dataset
   * attributes rendered by the section settings.
   *
   * @returns {{ color: FillStop[], light: FillStop[] }} The anchor stops
   */
  #parseStops() {
    return {
      color: [
        {
          color: this.#parseColor(this.dataset.colorStart, [255, 0, 0]),
          alpha: this.#parseAlpha(this.dataset.colorAlphaStart),
        },
        {
          color: this.#parseColor(this.dataset.colorMid, [208, 0, 255]),
          alpha: this.#parseAlpha(this.dataset.colorAlphaMid),
        },
        {
          color: this.#parseColor(this.dataset.colorEnd, [0, 47, 255]),
          alpha: this.#parseAlpha(this.dataset.colorAlphaEnd),
        },
      ],
      light: [
        {
          color: this.#parseColor(this.dataset.lightStart, [208, 152, 152]),
          alpha: this.#parseAlpha(this.dataset.lightAlphaStart),
        },
        {
          color: this.#parseColor(this.dataset.lightEnd, [182, 31, 31]),
          alpha: this.#parseAlpha(this.dataset.lightAlphaEnd),
        },
      ],
    };
  }

  /**
   * Parse a 0-100 alpha setting into a 0-1 float.
   *
   * @param {string | undefined} value - The alpha percentage
   * @returns {number} The alpha, 0 to 1
   */
  #parseAlpha(value) {
    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return 1;
    }

    return Math.min(Math.max(parsed, 0), 100) / 100;
  }

  /**
   * Linearly interpolate between two RGB triplets.
   *
   * @param {RgbColor} from - The start color
   * @param {RgbColor} to - The end color
   * @param {number} amount - The interpolation amount, 0 to 1
   * @returns {RgbColor} The interpolated color
   */
  #lerpColor(from, to, amount) {
    return from.map((channel, index) => Math.round(channel + (to[index] - channel) * amount));
  }

  /**
   * Compute the fill color and alpha for a slider progress within the
   * active mode.
   *
   * @param {number} progress - The slider progress, 0 to 1
   * @returns {{ color: RgbColor, alpha: number }} The fill color and alpha
   */
  #fillAt(progress) {
    if (this.activeMode !== 'color') {
      const [from, to] = this.colorStops.light;

      return {
        color: this.#lerpColor(from.color, to.color, progress),
        alpha: from.alpha + (to.alpha - from.alpha) * progress,
      };
    }

    const [start, mid, end] = this.colorStops.color;

    if (progress <= 0.5) {
      const amount = progress * 2;

      return {
        color: this.#lerpColor(start.color, mid.color, amount),
        alpha: start.alpha + (mid.alpha - start.alpha) * amount,
      };
    }

    const amount = (progress - 0.5) * 2;

    return {
      color: this.#lerpColor(mid.color, end.color, amount),
      alpha: mid.alpha + (end.alpha - mid.alpha) * amount,
    };
  }

  /**
   * Declarative mode button handler, wired via on:click="/setMode?mode=...".
   *
   * @param {{ mode?: string } | undefined} data - The parsed query data
   * @returns {void}
   */
  setMode(data) {
    if (this.dataset.modeDisplay !== 'both') return;

    this.#setMode(data && data.mode === 'light' ? 'light' : 'color');
  }

  /**
   * Switch the active mode: toggles the slider track gradient, the mode
   * buttons, and the mode copy.
   *
   * @param {'color' | 'light'} mode - The mode to apply
   * @param {{ sync?: boolean }} [options] - Whether to re-sync afterwards
   * @returns {void}
   */
  #setMode(mode, options) {
    this.activeMode = mode;
    this.dataset.activeMode = mode;

    const track = mode === 'color' ? this.dataset.colorTrack : this.dataset.lightTrack;

    if (track) {
      this.style.setProperty('--sutyo-tint-track', track);
    }

    this.querySelectorAll('[data-mode-btn]').forEach((button) => {
      const isActive = button instanceof HTMLElement && button.dataset.mode === mode;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.#updateModeCopy();

    if (!options || options.sync) {
      this.sync();
    }
  }

  /**
   * Swap the slider label text to match the active mode.
   *
   * @returns {void}
   */
  #updateModeCopy() {
    const copy = this.querySelector('[data-mode-copy]');

    if (!(copy instanceof HTMLElement)) {
      return;
    }

    const isColor = this.activeMode === 'color';

    const next = isColor ? copy.dataset.colorCopy || copy.dataset.fallbackCopy : copy.dataset.lightCopy || copy.dataset.fallbackCopy;
    const size = isColor ? this.dataset.colorCopySize : this.dataset.lightCopySize;

    copy.innerHTML = next || '';
    copy.style.setProperty('--copy-size', size ? `${size}px` : '');
  }

  /**
   * Sync the lens fill color with the slider value.
   * Called declaratively via on:input="/sync".
   */
  sync() {
    const { slider } = this.refs;
    const raw = (Number(slider.value) - Number(slider.min)) / (Number(slider.max) - Number(slider.min));
    const progress = Math.min(Math.max(raw, 0), 1);
    const { color, alpha } = this.#fillAt(progress);
    const [red, green, blue] = color;
    const topAlpha = Math.min(Math.max(alpha, 0), 1).toFixed(3);

    if (this.lensGradient <= 0) {
      this.style.setProperty('--tint-fill', `rgb(${red} ${green} ${blue} / ${topAlpha})`);
      return;
    }

    const bottomAlpha = Math.max(0, alpha * (1 - this.lensGradient)).toFixed(3);

    this.style.setProperty(
      '--tint-fill',
      `linear-gradient(180deg, rgb(${red} ${green} ${blue} / ${topAlpha}), rgb(${red} ${green} ${blue} / ${bottomAlpha}))`
    );
  }

  /**
   * Size the mode copy slot to the tallest of all configured copies, so
   * switching modes never changes the panel height and the copy always
   * stays inside the controls panel.
   *
   * @returns {void}
   */
  #measureCopySlot() {
    const copy = this.querySelector('[data-mode-copy]');

    if (!(copy instanceof HTMLElement)) {
      return;
    }

    const candidates = [
      { text: copy.dataset.colorCopy, size: this.dataset.colorCopySize },
      { text: copy.dataset.lightCopy, size: this.dataset.lightCopySize },
      { text: copy.dataset.fallbackCopy, size: null },
    ].filter((candidate) => typeof candidate.text === 'string' && candidate.text.length > 0);

    if (candidates.length === 0) {
      return;
    }

    const previousText = copy.innerHTML;
    const previousSize = copy.style.getPropertyValue('--copy-size');
    copy.style.height = 'auto';

    let tallest = 0;

    for (const candidate of candidates) {
      copy.innerHTML = candidate.text;
      copy.style.setProperty('--copy-size', candidate.size ? `${candidate.size}px` : '');
      tallest = Math.max(tallest, copy.getBoundingClientRect().height);
    }

    copy.innerHTML = previousText;
    copy.style.setProperty('--copy-size', previousSize);
    copy.style.height = `${Math.ceil(tallest)}px`;
  }

  /**
   * Mirror the sutyo-lens composed layout on both breakpoints: the media
   * card and the control panel form one vertically centered group, with
   * the panel pinned a fixed gap below the card. Falls back to the
   * stylesheet positioning whenever the composition does not fit.
   *
   * @returns {void}
   */
  #syncComposedLayout() {
    const card = this.querySelector('.sutyo-tint__stage');
    const lens = this.querySelector('.sutyo-tint__stack');
    const controls = this.querySelector('.sutyo-tint__controls');

    if (!(card instanceof HTMLElement) || !(controls instanceof HTMLElement)) {
      return;
    }

    const sectionHeight = this.getBoundingClientRect().height;

    if (!Number.isFinite(sectionHeight) || sectionHeight <= 0) {
      this.#resetComposedElement(card);
      this.#resetComposedElement(lens);
      this.#resetComposedElement(controls);
      return;
    }

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.#applyMobileComposition(card, lens, controls, sectionHeight);
      return;
    }

    this.#applyDesktopComposition(card, lens, controls, sectionHeight);
  }

  /**
   * Clear every inline positioning property set by the composed layout so
   * the stylesheet rules take over again.
   *
   * @param {Element | null} element - The element to reset
   * @returns {void}
   */
  #resetComposedElement(element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    element.style.top = '';
    element.style.right = '';
    element.style.bottom = '';
    element.style.left = '';
    element.style.width = '';
    element.style.height = '';
    element.style.transform = '';
    element.style.position = '';
    element.style.aspectRatio = '';
    element.style.justifyContent = '';
    element.style.marginTop = '';
    element.style.marginBottom = '';
  }

  /**
   * Desktop composition: 15/8 card centered in the space above the panel,
   * panel pinned 30px below the card.
   *
   * @param {HTMLElement} card - The media card element
   * @param {Element | null} lens - The glasses overlay element
   * @param {HTMLElement} controls - The control panel element
   * @param {number} sectionHeight - The rendered section height in px
   * @returns {void}
   */
  #applyDesktopComposition(card, lens, controls, sectionHeight) {
    this.#resetComposedElement(card);
    this.#resetComposedElement(lens);
    this.#resetComposedElement(controls);

    const cardHeight = card.getBoundingClientRect().height;
    const panelHeight = controls.offsetHeight;
    const gap = 30;
    const safeEdge = 12;
    const bottomOffset =
      Number.parseFloat(getComputedStyle(this).getPropertyValue('--sutyo-tint-controls-bottom')) || 20;
    const available = sectionHeight - (bottomOffset + panelHeight + gap);

    if (available < cardHeight + safeEdge) {
      return;
    }

    const cardTop = Math.max((available - cardHeight) / 2, safeEdge);

    card.style.top = `${Math.round(cardTop)}px`;
    card.style.transform = 'translateX(-50%)';
    controls.style.top = `${Math.round(cardTop + cardHeight + gap)}px`;
    controls.style.bottom = 'auto';
  }

  /**
   * Mobile composition mirroring sutyo-lens: square card sized to
   * min(viewport - 40, remaining height), card + panel centered as one
   * group, panel pinned 18px below the card. The glasses overlay fills the
   * card via the stylesheet (inset: 0), so no inline lens positioning is
   * needed here.
   *
   * @param {HTMLElement} card - The media card element
   * @param {Element | null} lens - The glasses overlay element
   * @param {HTMLElement} controls - The control panel element
   * @param {number} sectionHeight - The rendered section height in px
   * @returns {void}
   */
  #applyMobileComposition(card, lens, controls, sectionHeight) {
    this.#resetComposedElement(card);
    this.#resetComposedElement(lens);
    this.#resetComposedElement(controls);

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const panelHeight = controls.offsetHeight;
    const gap = 18;
    const safeEdge = 12;
    const frameSize = Math.max(
      220,
      Math.min(viewportWidth - 40, sectionHeight - panelHeight - gap - safeEdge - 12)
    );
    const groupHeight = frameSize + gap + panelHeight;

    if (frameSize < 220 || groupHeight > sectionHeight - safeEdge) {
      return;
    }

    const groupTop = Math.max((sectionHeight - groupHeight) / 2, safeEdge);
    const cardTop = Math.round(groupTop);
    const panelTop = Math.round(groupTop + frameSize + gap);

    card.style.width = `${frameSize}px`;
    card.style.height = `${frameSize}px`;
    card.style.top = `${cardTop}px`;
    card.style.left = '50%';
    card.style.transform = 'translateX(-50%)';

    controls.style.position = 'absolute';
    controls.style.top = `${panelTop}px`;
    controls.style.left = '50%';
    controls.style.transform = 'translateX(-50%)';
    controls.style.width = 'min(calc(100% - 24px), 566px)';
  }

  /**
   * Boost the alpha channel of the glasses image(s) when they were exported
   * at a low opacity. The gain self-calibrates to the strongest alpha found
   * in each image, so already-opaque exports pass through untouched. The
   * boosted bitmaps replace the desktop and mobile fill masks separately so
   * the correct mask stays active on each breakpoint.
   *
   * @returns {Promise<void>}
   */
  async #amplifyGlassesAlpha() {
    const desktopUrl = this.dataset.glassesDesktop;
    const mobileUrl = this.dataset.glassesMobile;

    if (!desktopUrl) {
      return;
    }

    const desktopBoosted = await this.#boostImageAlpha(desktopUrl);

    if (desktopBoosted) {
      this.style.setProperty('--sutyo-tint-mask', `url('${desktopBoosted}')`);
      this.style.setProperty('--sutyo-tint-mask-desktop', `url('${desktopBoosted}')`);
    }

    if (mobileUrl && mobileUrl !== desktopUrl) {
      const mobileBoosted = await this.#boostImageAlpha(mobileUrl);

      if (mobileBoosted) {
        this.style.setProperty('--sutyo-tint-mask-mobile', `url('${mobileBoosted}')`);
      }
    } else if (desktopBoosted) {
      this.style.setProperty('--sutyo-tint-mask-mobile', `url('${desktopBoosted}')`);
    }
  }

  /**
   * Fetch a glasses image and boost its alpha channel in place. Returns the
   * boosted image as a PNG data URL, or null when no boost is needed or the
   * image cannot be decoded.
   *
   * @param {string} source - The glasses image URL
   * @returns {Promise<string | null>} The boosted data URL, or null
   */
  async #boostImageAlpha(source) {
    try {
      const response = await fetch(source, { mode: 'cors' });

      if (!response.ok) {
        return null;
      }

      const bitmap = await createImageBitmap(await response.blob());
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;

      const context = canvas.getContext('2d', { willReadFrequently: true });

      if (!context) {
        bitmap.close();
        return null;
      }

      context.drawImage(bitmap, 0, 0);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const alphas = [];

      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] > 0) {
          alphas.push(imageData.data[i]);
        }
      }

      if (alphas.length === 0) {
        bitmap.close();
        return null;
      }

      alphas.sort((a, b) => a - b);

      const percentile = alphas[Math.min(Math.floor(alphas.length * 0.9), alphas.length - 1)];

      if (percentile >= 250) {
        bitmap.close();
        return null;
      }

      const gain = Math.min(255 / Math.max(percentile, 1), 20);

      for (let i = 3; i < imageData.data.length; i += 4) {
        const alpha = imageData.data[i];
        imageData.data[i] = alpha < 3 ? 0 : Math.min(255, Math.round(alpha * gain));
      }

      context.putImageData(imageData, 0, 0);
      bitmap.close();

      return canvas.toDataURL('image/png');
    } catch {
      /* CORS or decode failure: keep the original image and mask. */
      return null;
    }
  }

  /**
   * Play a one-time nudge animation when the block scrolls into view,
   * hinting that the slider is draggable.
   */
  #setupHintAnimation() {
    if (this.dataset.hintAnimation !== 'true') return;
    if (!window.IntersectionObserver || prefersReducedMotion()) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.hasAnimated) continue;

          this.hasAnimated = true;
          oncePerEditorSession(this, 'sutyo-lens-tint-animated', () => this.#animateHint());

          this.intersectionObserver.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    this.intersectionObserver.observe(this);
  }

  #animateHint() {
    const { slider } = this.refs;
    const initial = Number(slider.value);
    const low = Math.max(Number(slider.min), initial - 20);
    const high = Math.min(Number(slider.max), initial + 20);

    const steps = [
      [low, 150],
      [high, 650],
      [initial, 1150],
    ];

    this.style.setProperty('--tint-transition', 'background-color 0.4s ease-in-out');

    for (const [value, delay] of steps) {
      setTimeout(() => {
        slider.value = String(value);
        this.sync();
      }, delay);
    }

    setTimeout(() => {
      this.style.setProperty('--tint-transition', 'none');
    }, 1600);
  }
}

if (!customElements.get('sutyo-lens-tint-component')) {
  customElements.define('sutyo-lens-tint-component', SutyoLensTintComponent);
}
